// Minimap HUD — top-right of the screen. Rotates with player heading so the
// map always feels "north is where you're facing". Uses map bounds + walls
// from the current match to draw a schematic overhead view.
import { useEffect, useMemo, useRef } from "react";
import { useMatchStore } from "@/stores/matchStore";
import { matchRefs } from "@/game/systems/matchRefs";

const SIZE = 180; // px
const MARGIN = 24;

// Registry mirrored from FPSGame so the HUD can pull walls/bounds without a prop chain
import { TRAINING_YARD_META } from "@/game/maps/TrainingYard";
import { FACTORY_META } from "@/game/maps/Factory";
import { WAREHOUSE_META } from "@/game/maps/Warehouse";
import { DESERT_BASE_META } from "@/game/maps/DesertBase";

const META = {
    training_yard: TRAINING_YARD_META,
    factory: FACTORY_META,
    warehouse: WAREHOUSE_META,
    desert_base: DESERT_BASE_META,
};

export default function Minimap() {
    const mapId = useMatchStore((s) => s.mapId);
    const meta = META[mapId] || TRAINING_YARD_META;

    const canvasRef = useRef();
    const anim = useRef(0);

    // Precompute normalized bounds
    const { minX, maxX, minZ, maxZ } = meta.bounds;
    const spanX = maxX - minX;
    const spanZ = maxZ - minZ;

    const worldToMap = useMemo(
        () => (x, z) => {
            const nx = (x - minX) / spanX;
            const nz = (z - minZ) / spanZ;
            return [nx * SIZE, nz * SIZE];
        },
        [minX, minZ, spanX, spanZ]
    );

    useEffect(() => {
        let running = true;
        const draw = () => {
            if (!running) return;
            const canvas = canvasRef.current;
            if (!canvas) {
                anim.current = requestAnimationFrame(draw);
                return;
            }
            const ctx = canvas.getContext("2d");
            const half = SIZE / 2;

            // Clear
            ctx.clearRect(0, 0, SIZE, SIZE);

            // Backdrop
            ctx.save();
            ctx.fillStyle = "rgba(6,10,16,0.78)";
            ctx.fillRect(0, 0, SIZE, SIZE);
            ctx.restore();

            // Border grid
            ctx.strokeStyle = "rgba(255,255,255,0.06)";
            ctx.lineWidth = 1;
            for (let i = 1; i < 6; i++) {
                ctx.beginPath();
                ctx.moveTo((SIZE / 6) * i, 0);
                ctx.lineTo((SIZE / 6) * i, SIZE);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(0, (SIZE / 6) * i);
                ctx.lineTo(SIZE, (SIZE / 6) * i);
                ctx.stroke();
            }

            // Player transform (rotate the canvas so player looks up)
            const p = matchRefs.player;
            const yaw = p?.rotation?.y ?? 0;
            const px = p?.position?.x ?? 0;
            const pz = p?.position?.z ?? 0;
            const [pmx, pmz] = worldToMap(px, pz);

            ctx.save();
            ctx.translate(half, half);
            ctx.rotate(-yaw);
            ctx.translate(-pmx, -pmz);

            // Draw walls as filled boxes (only interior — skip perimeter for readability)
            ctx.fillStyle = "rgba(160,180,200,0.35)";
            for (let i = 0; i < meta.walls.length; i++) {
                const w = meta.walls[i];
                // Skip perimeter (first 4 walls are the border)
                if (i < 4) continue;
                const [x1, z1] = worldToMap(w.min[0], w.min[2]);
                const [x2, z2] = worldToMap(w.max[0], w.max[2]);
                ctx.fillRect(Math.min(x1, x2), Math.min(z1, z2), Math.abs(x2 - x1), Math.abs(z2 - z1));
            }

            // Perimeter outline
            ctx.strokeStyle = "rgba(255,255,255,0.35)";
            ctx.lineWidth = 1;
            const [b1x, b1z] = worldToMap(minX, minZ);
            const [b2x, b2z] = worldToMap(maxX, maxZ);
            ctx.strokeRect(b1x, b1z, b2x - b1x, b2z - b1z);

            // Bots (allies cyan, enemies red — if visible)
            const bots = matchRefs.bots || [];
            for (const b of bots) {
                if (!b.alive) continue;
                const [bx, bz] = worldToMap(b.position[0], b.position[2]);
                ctx.fillStyle = b.team === "enemy" ? "#ff5060" : "#00e5ff";
                ctx.beginPath();
                ctx.arc(bx, bz, 3, 0, Math.PI * 2);
                ctx.fill();
            }

            ctx.restore();

            // Player arrow (always centered pointing up)
            ctx.save();
            ctx.translate(half, half);
            ctx.fillStyle = "#ffce4a";
            ctx.strokeStyle = "rgba(0,0,0,0.5)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, -8);
            ctx.lineTo(6, 6);
            ctx.lineTo(0, 3);
            ctx.lineTo(-6, 6);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.restore();

            // Border frame
            ctx.strokeStyle = "rgba(255,154,74,0.55)";
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, SIZE - 1, SIZE - 1);

            anim.current = requestAnimationFrame(draw);
        };
        anim.current = requestAnimationFrame(draw);
        return () => {
            running = false;
            cancelAnimationFrame(anim.current);
        };
    }, [worldToMap, meta, minX, minZ, maxX, maxZ]);

    return (
        <div
            data-testid="minimap"
            className="fixed z-30 pointer-events-none"
            style={{
                top: MARGIN,
                right: MARGIN,
                width: SIZE,
                height: SIZE + 22,
            }}
        >
            <div className="axis-mono text-[9px] tracking-[0.4em] text-orange-300/80 mb-1 flex items-center justify-between">
                <span>{meta.name?.toUpperCase()}</span>
                <span className="text-white/50">{meta.preview?.environment}</span>
            </div>
            <canvas
                ref={canvasRef}
                width={SIZE}
                height={SIZE}
                style={{
                    display: "block",
                    borderRadius: 2,
                    boxShadow: "0 0 24px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.05)",
                    background: "rgba(6,10,16,0.6)",
                }}
            />
        </div>
    );
}
